import axios from "axios";
import httpClient from "../../clients/httpClient";
import { IQuery } from "../../interfaces/meta.interface";
import { IInviteStaffToOrganizationPayload } from "../../interfaces/organization.interface";
import { IUserInvitation } from "../../interfaces/user-invitation.interface";
import { IUser } from "../../interfaces/user.interface";
import { IAdminOrganizationQuery } from "../../state/types/admin-organization";

export const getStaff = (query?: IAdminOrganizationQuery) => {
    if (query?.id) {
        return httpClient.get(`/api/v1/admin/organizations/${query.id}/staff`, {
            params: {
                page: query?.page,
                per_page: query?.perPage,
                include: 'role,storeRestrictions,tenant',
                search: query?.search,
                order_by: query?.orderBy,
                direction: query?.sortDirection,
            }
        });
    }

    return httpClient.get(`/api/v1/organizations/staff`, {
        params: {
            page: query?.page,
            per_page: query?.perPage,
            include: 'role,storeRestrictions',
            search: query?.search,
            order_by: query?.orderBy,
            direction: query?.sortDirection,
        }
    });
}

export const getInvitedStaff = (query?: IQuery) => {
    return httpClient.get(`/api/v1/invitations`, {
        params: {
            page: query?.page,
            per_page: query?.perPage,
            search: query?.search,
            order_by: query?.orderBy,
            direction: query?.sortDirection,
        }
    });
}

export const removeStaff = (organization: number | undefined, staff: number) => {
    return httpClient.delete(`/api/v1/admin/organizations/${organization}/staff/${staff}`);
}
export const organizationRemoveStaff = (staff: number) => {
    return httpClient.delete(`/api/v1/organizations/staff/${staff}`);
}

export const inviteStaff = (payload: IInviteStaffToOrganizationPayload) => {
    return httpClient.post('/api/v1/admin/invitations/', payload);
}

// Role and store fields are only sent where the form offers them; omitting
// role_id lets the server default to Organization Admin.
export const organizationInviteStaff = (payload: IInviteStaffToOrganizationPayload) => {
    return httpClient.post<IUserInvitation | IUser>('/api/v1/invitations', {
        email: payload.email,
        ...(payload.role_id ? { role_id: Number(payload.role_id) } : {}),
        ...(payload.is_restricted_to_stores !== undefined
            ? {
                is_restricted_to_stores: payload.is_restricted_to_stores,
                store_ids: payload.store_ids ?? [],
            }
            : {}),
    });
}

// Raw-axios fetch of pending invitations, bypassing httpClient. httpClient
// dedupes by aborting the earlier request to the same URL, and an aborted
// request resolves to a never-settling promise. The org dashboard GETs this
// endpoint while also POSTing to it to send an invite — routing both through
// httpClient lets the POST abort an in-flight GET and hang it forever, leaving
// the tile stuck on its skeleton. Same reasoning as fetchProjectAttributesRaw.
export const getPendingInvitationsRaw = async (perPage = 50): Promise<IUserInvitation[]> => {
    const org = localStorage.getItem("org");
    const protocol = import.meta.env.VITE_BACKEND_PROTOCOL ?? "https";
    const baseURL = org
        ? `${protocol}://${org}.${import.meta.env.VITE_ORG_API_ENDPOINT}`
        : "";
    const token = JSON.parse(localStorage.getItem("token") || "{}")?.access_token;
    const response = await axios.get(`${baseURL}/api/v1/invitations`, {
        params: { page: 1, per_page: perPage },
        headers: {
            Authorization: token ? `Bearer ${token}` : "",
            Accept: "application/json",
        },
    });
    const body = response.data;
    return Array.isArray(body?.data) ? body.data : (body?.data?.data ?? []);
}

export const organizationResendInvitation = (id: number) => {
    return httpClient.patch(`/api/v1/invitations/${id}/resend`);
}

export const organizationCancelInvite = (id: number) => {
    return httpClient.delete(`/api/v1/invitations/${id}`);
}

export const cancelInvite = (id: number) => {
    return httpClient.delete(`/api/v1/admin/invitations/${id}`);
}
